/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeAutocomplete,
  DimeButton,
  DimeCombobox,
  DimeComboboxChip,
  DimeComboboxChips,
  DimeComboboxChipsInput,
  DimeComboboxContent,
  DimeComboboxEmpty,
  DimeComboboxGroup,
  DimeComboboxInput,
  DimeComboboxItem,
  DimeComboboxLabel,
  DimeComboboxList,
  DimeComboboxSeparator,
  DimeComboboxTrigger,
  DimeComboboxValue,
  DimeSeparator,
  EMPTY_OPTION_LABEL,
  useDimeComboboxAnchor,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/combobox')({
  component: ComboboxDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

// ── Data ───────────────────────────────────────────────────────────────────────

const STATUSES = ['Active', 'Inactive', 'Pending', 'Archived'] as const;

const TAGS = ['Urgent', 'Needs Review', 'Blocked', 'In Progress', 'On Hold', 'Approved'] as const;

const ACCOUNT_GROUPS: { heading: string; items: string[] }[] = [
  { heading: 'Assets', items: ['Cash', 'Accounts Receivable', 'Inventory'] },
  { heading: 'Liabilities', items: ['Accounts Payable', 'Accrued Liabilities'] },
  { heading: 'Revenue', items: ['Sales Revenue', 'Service Revenue'] },
];

const ALL_ACCOUNTS = ACCOUNT_GROUPS.flatMap((group) => group.items);

interface TeamMember {
  value: string;
  name: string;
  role: string;
  initials: string;
}

const TEAM: TeamMember[] = [
  { value: 'alice', name: 'Alice Smith', role: 'Engineering', initials: 'AS' },
  { value: 'bob', name: 'Bob Chen', role: 'Design', initials: 'BC' },
  { value: 'carol', name: 'Carol White', role: 'Product', initials: 'CW' },
  { value: 'david', name: 'David Park', role: 'Engineering', initials: 'DP' },
];

function renderStatusItem(item: (typeof STATUSES)[number]) {
  return (
    <DimeComboboxItem key={item} value={item}>
      {item}
    </DimeComboboxItem>
  );
}

// ── Examples ───────────────────────────────────────────────────────────────────

function BasicExample() {
  return (
    <div className="w-full max-w-xs">
      <DimeCombobox items={STATUSES}>
        <DimeComboboxInput placeholder="Select status..." />
        <DimeComboboxContent>
          <DimeComboboxEmpty>No results found.</DimeComboboxEmpty>
          <DimeComboboxList>{renderStatusItem}</DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
    </div>
  );
}

const CURRENCIES = [
  { value: 'usd', label: 'USD' },
  { value: 'cad', label: 'CAD' },
  { value: 'eur', label: 'EUR' },
];

function OptionComboboxExample() {
  const [value, setValue] = React.useState('');

  return (
    <div className="w-full max-w-xs">
      <label htmlFor="ds-currency" className="mb-1.5 block text-sm font-medium">
        Currency
      </label>
      <DimeAutocomplete
        id="ds-currency"
        placeholder="Select…"
        emptyMessage="No currencies found."
        emptyOptionLabel={EMPTY_OPTION_LABEL}
        options={CURRENCIES}
        value={value}
        onValueChange={setValue}
      />
    </div>
  );
}

const CURRENCY_NAMES: Record<string, string> = {
  usd: 'US dollar',
  cad: 'Canadian dollar',
  eur: 'Euro',
};

function ServerSearchAutocompleteExample() {
  const [value, setValue] = React.useState('');
  const [query, setQuery] = React.useState('');
  const options = CURRENCIES.filter((currency) =>
    CURRENCY_NAMES[currency.value].toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="w-full max-w-xs">
      <label htmlFor="ds-currency-search" className="mb-1.5 block text-sm font-medium">
        Currency (search by name)
      </label>
      <DimeAutocomplete
        id="ds-currency-search"
        placeholder="Type a currency name…"
        emptyMessage="No currencies found."
        options={options}
        value={value}
        onValueChange={setValue}
        onSearchChange={setQuery}
      />
    </div>
  );
}

const BRANDS = [
  { value: 'acme', label: 'Acme' },
  { value: 'globex', label: 'Globex' },
  { value: 'initech', label: 'Initech' },
  { value: 'umbrella', label: 'Umbrella' },
];

function MultipleAutocompleteExample({ chips = false }: Readonly<{ chips?: boolean }>) {
  const [value, setValue] = React.useState<string[]>([]);
  const id = chips ? 'ds-brands-chips' : 'ds-brands';

  return (
    <div className="w-full max-w-xs">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
        {chips ? 'Brands (chips)' : 'Brands'}
      </label>
      <DimeAutocomplete
        id={id}
        multiple
        chips={chips}
        placeholder="Select brands…"
        emptyMessage="No brands found."
        options={BRANDS}
        value={value}
        onValueChange={setValue}
      />
    </div>
  );
}

function FooterActionAutocompleteExample() {
  const [brands, setBrands] = React.useState(BRANDS);
  const [value, setValue] = React.useState('');

  return (
    <div className="w-full max-w-xs">
      <label htmlFor="ds-brand-footer" className="mb-1.5 block text-sm font-medium">
        Brand
      </label>
      <DimeAutocomplete
        id="ds-brand-footer"
        placeholder="Select a brand…"
        emptyMessage="No brands found."
        options={brands}
        value={value}
        onValueChange={setValue}
        footerAction={{
          label: '+ New Brand',
          onSelect: () => {
            const added = {
              value: `brand-${brands.length + 1}`,
              label: `Brand ${brands.length + 1}`,
            };
            setBrands([...brands, added]);
            setValue(added.value);
          },
        }}
      />
    </div>
  );
}

function NoSelectAllAutocompleteExample() {
  const [value, setValue] = React.useState<string[]>([]);

  return (
    <div className="w-full max-w-xs">
      <label htmlFor="ds-brand-grants" className="mb-1.5 block text-sm font-medium">
        Brand access
      </label>
      <DimeAutocomplete
        id="ds-brand-grants"
        multiple
        selectAll={false}
        placeholder="Grant brands…"
        emptyMessage="No brands found."
        options={BRANDS}
        value={value}
        onValueChange={setValue}
      />
    </div>
  );
}

function MultipleExample() {
  const [value, setValue] = React.useState<string[]>([]);
  const anchor = useDimeComboboxAnchor();

  return (
    <div className="w-full max-w-xs">
      <DimeCombobox multiple items={TAGS} value={value} onValueChange={setValue}>
        <DimeComboboxChips ref={anchor}>
          <DimeComboboxValue>
            {value.map((item) => (
              <DimeComboboxChip key={item}>{item}</DimeComboboxChip>
            ))}
          </DimeComboboxValue>
          <DimeComboboxChipsInput placeholder="Add tag..." />
        </DimeComboboxChips>
        <DimeComboboxContent anchor={anchor}>
          <DimeComboboxEmpty>No tags found.</DimeComboboxEmpty>
          <DimeComboboxList>
            {(item) => (
              <DimeComboboxItem key={item} value={item}>
                {item}
              </DimeComboboxItem>
            )}
          </DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
    </div>
  );
}

function ClearExample() {
  return (
    <div className="w-full max-w-xs">
      <DimeCombobox items={STATUSES} defaultValue="Active">
        <DimeComboboxInput placeholder="Select status..." showClear />
        <DimeComboboxContent>
          <DimeComboboxEmpty>No results found.</DimeComboboxEmpty>
          <DimeComboboxList>{renderStatusItem}</DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
    </div>
  );
}

function GroupsExample() {
  return (
    <div className="w-full max-w-xs">
      <DimeCombobox items={ALL_ACCOUNTS}>
        <DimeComboboxInput placeholder="Select account..." />
        <DimeComboboxContent>
          <DimeComboboxEmpty>No accounts found.</DimeComboboxEmpty>
          <DimeComboboxList>
            {ACCOUNT_GROUPS.map((group, idx) => (
              <React.Fragment key={group.heading}>
                {idx > 0 && <DimeComboboxSeparator />}
                <DimeComboboxGroup>
                  <DimeComboboxLabel>{group.heading}</DimeComboboxLabel>
                  {group.items.map((item) => (
                    <DimeComboboxItem key={item} value={item}>
                      {item}
                    </DimeComboboxItem>
                  ))}
                </DimeComboboxGroup>
              </React.Fragment>
            ))}
          </DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
    </div>
  );
}

function CustomItemsExample() {
  return (
    <div className="w-full max-w-xs">
      <DimeCombobox items={TEAM} itemToStringValue={(member) => (member as TeamMember).name}>
        <DimeComboboxInput placeholder="Search team members..." />
        <DimeComboboxContent>
          <DimeComboboxEmpty>No members found.</DimeComboboxEmpty>
          <DimeComboboxList>
            {(item) => {
              const member = item as TeamMember;
              return (
                <DimeComboboxItem key={member.value} value={member}>
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                    {member.initials}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm">{member.name}</span>
                    <span className="text-xs text-muted-foreground">{member.role}</span>
                  </span>
                </DimeComboboxItem>
              );
            }}
          </DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
    </div>
  );
}

function InvalidExample() {
  return (
    <div className="w-full max-w-xs space-y-1.5">
      <DimeCombobox items={STATUSES}>
        <DimeComboboxInput placeholder="Select status..." aria-invalid />
        <DimeComboboxContent>
          <DimeComboboxEmpty>No results found.</DimeComboboxEmpty>
          <DimeComboboxList>{renderStatusItem}</DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
      <p className="text-xs text-destructive">Status is required.</p>
    </div>
  );
}

function DisabledExample() {
  return (
    <div className="w-full max-w-xs">
      <DimeCombobox items={STATUSES} defaultValue="Active">
        <DimeComboboxInput placeholder="Select status..." disabled />
        <DimeComboboxContent>
          <DimeComboboxEmpty>No results found.</DimeComboboxEmpty>
          <DimeComboboxList>{renderStatusItem}</DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
    </div>
  );
}

function AutoHighlightExample() {
  return (
    <div className="w-full max-w-xs">
      <DimeCombobox items={STATUSES} autoHighlight>
        <DimeComboboxInput placeholder="Select status..." />
        <DimeComboboxContent>
          <DimeComboboxEmpty>No results found.</DimeComboboxEmpty>
          <DimeComboboxList>{renderStatusItem}</DimeComboboxList>
        </DimeComboboxContent>
      </DimeCombobox>
    </div>
  );
}

function PopupExample() {
  const [value, setValue] = React.useState('');

  return (
    <DimeCombobox
      items={STATUSES}
      value={value}
      onValueChange={(val) => setValue((val as string) ?? '')}
    >
      <DimeButton asChild variant="outline">
        <DimeComboboxTrigger aria-label={value || 'Select status'}>
          {value || 'Select status'}
        </DimeComboboxTrigger>
      </DimeButton>
      <DimeComboboxContent>
        <DimeComboboxInput placeholder="Search..." showTrigger={false} showClear className="mb-0" />
        <DimeComboboxEmpty>No results found.</DimeComboboxEmpty>
        <DimeComboboxList>{renderStatusItem}</DimeComboboxList>
      </DimeComboboxContent>
    </DimeCombobox>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function ComboboxDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Combobox</h1>
      <p className="mb-8 text-muted-foreground">
        An autocomplete input with a list of suggestions. Built on{' '}
        <code className="font-mono text-sm">@base-ui/react</code>. The input is always visible —
        type to filter, select to close. App code picks values with{' '}
        <code className="font-mono text-sm">DimeAutocomplete</code> (or{' '}
        <code className="font-mono text-sm">DimeFilterCombobox</code> on a list toolbar); the{' '}
        <code className="font-mono text-sm">DimeCombobox</code> sections further down are the
        primitive those are built on, for use inside the design system.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <BasicExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">items</code> to the root and render each item inside{' '}
          <code className="font-mono">DimeComboboxList</code> using a render function.
        </p>
      </DocsSection>

      <DocsSection title="DimeAutocomplete (form convenience)">
        <Preview>
          <OptionComboboxExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The picker for one value or several, in a form or anywhere else. Pass{' '}
          <code className="font-mono">options</code> with string{' '}
          <code className="font-mono">value</code> /{' '}
          <code className="font-mono">onValueChange</code> (
          <code className="font-mono">{'{ value, label }'}</code>, optional empty row). Use it
          rather than hand-wiring <code className="font-mono">DimeCombobox</code>'s compound pieces;
          if it can't do what a screen needs, extend it.
        </p>
      </DocsSection>

      <DocsSection title="DimeAutocomplete (server search)">
        <Preview>
          <ServerSearchAutocompleteExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          With <code className="font-mono">onSearchChange</code>, options are shown as given (not
          label-filtered), so the caller filters them, e.g. on the server.
        </p>
      </DocsSection>

      <DocsSection title="DimeAutocomplete (several)">
        <Preview>
          <MultipleAutocompleteExample />
          <MultipleAutocompleteExample chips />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          With <code className="font-mono">multiple</code>, Select All heads the list once two or
          more options can be picked, while nothing is typed in the search. It picks every option,
          and picking it again while everything is picked clears them. The closed field names the
          picks, or counts them once they no longer fit; pass{' '}
          <code className="font-mono">chips</code> to show them as removable chips instead.
        </p>
      </DocsSection>

      <DocsSection title="DimeAutocomplete (footer action, no Select All)">
        <Preview>
          <FooterActionAutocompleteExample />
          <NoSelectAllAutocompleteExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">footerAction</code> adds a row under the list that does
          something rather than picking a value, such as &ldquo;+ New Contact&rdquo;; with the list
          open, Tab reaches it and Tab again moves on. Pass{' '}
          <code className="font-mono">selectAll={'{false}'}</code> where picking every option at
          once would be wrong, such as access grants, or where blank already means all.
        </p>
      </DocsSection>

      <DocsSection title="Multiple">
        <Preview>
          <MultipleExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Add <code className="font-mono">multiple</code> and use{' '}
          <code className="font-mono">DimeComboboxChips</code> with{' '}
          <code className="font-mono">DimeComboboxValue</code> to render selected chips.
        </p>
      </DocsSection>

      <DocsSection title="Clear button">
        <Preview>
          <ClearExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">showClear</code> to{' '}
          <code className="font-mono">DimeComboboxInput</code> to show an × button when a value is
          selected.
        </p>
      </DocsSection>

      <DocsSection title="Groups">
        <Preview>
          <GroupsExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeComboboxGroup</code>,{' '}
          <code className="font-mono">DimeComboboxLabel</code>, and{' '}
          <code className="font-mono">DimeComboboxSeparator</code> to organise items into labelled
          sections.
        </p>
      </DocsSection>

      <DocsSection title="Custom items">
        <Preview>
          <CustomItemsExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass objects as <code className="font-mono">items</code> and use{' '}
          <code className="font-mono">itemToStringValue</code> to tell the combobox which field to
          filter and display. Render any content inside{' '}
          <code className="font-mono">DimeComboboxItem</code>.
        </p>
      </DocsSection>

      <DocsSection title="Invalid">
        <Preview>
          <InvalidExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">aria-invalid</code> to{' '}
          <code className="font-mono">DimeComboboxInput</code> for destructive border styling.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DisabledExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> to{' '}
          <code className="font-mono">DimeComboboxInput</code> to prevent interaction.
        </p>
      </DocsSection>

      <DocsSection title="Auto highlight">
        <Preview>
          <AutoHighlightExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">autoHighlight</code> to automatically highlight the first
          matching item as you type.
        </p>
      </DocsSection>

      <DocsSection title="Popup">
        <Preview>
          <PopupExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Trigger from a button using <code className="font-mono">DimeComboboxTrigger</code> and
          move <code className="font-mono">DimeComboboxInput</code> inside the content.
        </p>
      </DocsSection>
    </div>
  );
}
